import { useCallback, useEffect, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import Placeholder from "@tiptap/extension-placeholder";
import { 
  Bold, 
  Italic, 
  Underline as UnderlineIcon, 
  Link as LinkIcon,
  List,
  ListOrdered,
  Heading2,
  Undo,
  Redo,
  Code,
  Quote,
  Minus,
  X,
  Monitor,
  Smartphone,
  Variable,
  ChevronDown,
  Check,
  Send,
  Loader2,
  Eye,
  EyeOff
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Switch } from "./ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "./ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "./ui/popover";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from "./ui/collapsible";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "./ui/tooltip";

// Toolbar button component
const ToolbarButton = ({ onClick, isActive, disabled, children, tooltip }) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={onClick}
          disabled={disabled}
          className={`p-2 rounded-md transition-colors ${
            isActive 
              ? "bg-primary text-primary-foreground" 
              : "hover:bg-muted"
          } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
        >
          {children}
        </button>
      </TooltipTrigger>
      <TooltipContent side="bottom">{tooltip}</TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

// Link dialog component
const LinkDialog = ({ editor, onClose }) => {
  const [url, setUrl] = useState("");
  
  const handleSubmit = (e) => {
    e.preventDefault();
    if (url) {
      editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
    } else {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
    }
    onClose();
  };
  
  return (
    <div className="p-2 space-y-2">
      <Label className="text-xs">Link URL</Label>
      <Input
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        placeholder="https://example.com"
        className="h-8 text-sm"
      />
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button type="button" size="sm" onClick={handleSubmit}>
          Apply
        </Button>
      </div>
    </div>
  );
};

// Sample data for preview
const SAMPLE_DATA = {
  client_name: "Sarah Johnson",
  client_first_name: "Sarah",
  client_email: "sarah@example.com",
  event_type: "Wedding Photography",
  event_date: "March 15, 2026",
  event_time: "2:00 PM",
  venue: "The Grand Ballroom, Downtown",
  photographer_name: "Your Studio Name",
  photographer_email: "hello@yourstudio.com",
  photographer_phone: "(555) 123-4567",
  quote_total: "$2,500.00",
  deposit_amount: "$500.00",
  balance_due: "$2,000.00",
  portal_link: '<a href="#" style="display:inline-block;padding:12px 24px;background:#6366f1;color:white;text-decoration:none;border-radius:6px;">View Your Booking</a>',
  gallery_link: '<a href="#" style="display:inline-block;padding:12px 24px;background:#6366f1;color:white;text-decoration:none;border-radius:6px;">View Your Gallery</a>',
  days_until_event: "7"
};

// Convert TipTap JSON to HTML string
const convertToHtml = (content) => {
  if (!content) return "";
  
  // If it's already a string, return it
  if (typeof content === "string") return content;
  
  // Convert TipTap JSON to HTML
  let html = "";
  
  const processNode = (node) => {
    if (!node) return "";
    
    let result = "";
    const text = node.text || "";
    
    // Handle marks (formatting)
    if (node.marks) {
      let wrappedText = text;
      node.marks.forEach(mark => {
        switch (mark.type) {
          case "bold":
            wrappedText = `<strong>${wrappedText}</strong>`;
            break;
          case "italic":
            wrappedText = `<em>${wrappedText}</em>`;
            break;
          case "underline":
            wrappedText = `<u>${wrappedText}</u>`;
            break;
          case "link":
            wrappedText = `<a href="${mark.attrs?.href || "#"}" style="color:#6366f1;">${wrappedText}</a>`;
            break;
          case "code":
            wrappedText = `<code style="background:#1e1e2e;padding:2px 6px;border-radius:4px;">${wrappedText}</code>`;
            break;
          default:
            break;
        }
      });
      return wrappedText;
    }
    
    if (node.type === "text") {
      return text;
    }
    
    // Process children
    const children = (node.content || []).map(processNode).join("");
    
    switch (node.type) {
      case "doc":
        return children;
      case "paragraph":
        return `<p style="margin:0 0 16px 0;line-height:1.6;">${children || "<br>"}</p>`;
      case "heading":
        const level = node.attrs?.level || 2;
        return `<h${level} style="margin:0 0 12px 0;font-weight:600;">${children}</h${level}>`;
      case "bulletList":
        return `<ul style="margin:0 0 16px 0;padding-left:24px;">${children}</ul>`;
      case "orderedList":
        return `<ol style="margin:0 0 16px 0;padding-left:24px;">${children}</ol>`;
      case "listItem":
        return `<li style="margin-bottom:4px;">${children}</li>`;
      case "blockquote":
        return `<blockquote style="border-left:3px solid #6366f1;padding-left:16px;margin:0 0 16px 0;color:#888;">${children}</blockquote>`;
      case "codeBlock":
        return `<pre style="background:#1e1e2e;padding:16px;border-radius:8px;overflow-x:auto;margin:0 0 16px 0;"><code>${children}</code></pre>`;
      case "horizontalRule":
        return `<hr style="border:none;border-top:1px solid #333;margin:24px 0;">`;
      case "hardBreak":
        return "<br>";
      default:
        return children;
    }
  };
  
  html = processNode(content);
  return html;
};

// Substitute variables in content
const substituteVariables = (html, variables = SAMPLE_DATA) => {
  let result = html;
  Object.entries(variables).forEach(([key, value]) => {
    const regex = new RegExp(`{{${key}}}`, "g");
    result = result.replace(regex, value);
  });
  return result;
};

const EmailTemplateEditor = ({
  template,
  onSave,
  onSendTest,
  onCancel,
  triggers = [],
  variables = [],
  saving = false,
  sendingTest = false
}) => {
  const [name, setName] = useState(template?.name || "");
  const [subject, setSubject] = useState(template?.subject || "");
  const [triggerType, setTriggerType] = useState(template?.trigger_type || "none");
  const [sendDelayHours, setSendDelayHours] = useState(template?.send_delay_hours || 0);
  const [isActive, setIsActive] = useState(template?.is_active !== false);
  const [previewDevice, setPreviewDevice] = useState("desktop");
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [automationOpen, setAutomationOpen] = useState(false);
  const [variablesOpen, setVariablesOpen] = useState(false);
  // Mobile tab state - "edit" or "preview"
  const [mobileTab, setMobileTab] = useState("edit");
  
  // Initialize TipTap editor
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] }
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: "text-primary underline"
        }
      }),
      Placeholder.configure({
        placeholder: "Start writing your email content..."
      })
    ],
    content: template?.body || "",
    editorProps: {
      attributes: {
        class: "prose prose-invert max-w-none min-h-[300px] p-4 focus:outline-none"
      }
    }
  });
  
  // Get current content as HTML
  const getHtmlContent = useCallback(() => {
    if (!editor) return "";
    const json = editor.getJSON();
    return convertToHtml(json);
  }, [editor]);
  
  // Get preview HTML
  const getPreviewHtml = useCallback(() => {
    const html = getHtmlContent();
    const substituted = substituteVariables(html);
    
    return `
      <!DOCTYPE html>
      <html>
        <head>
          <style>
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              background: #1a1a2e;
              color: #e4e4e7;
              margin: 0;
              padding: 24px;
              line-height: 1.6;
            }
            a { color: #6366f1; }
          </style>
        </head>
        <body>
          ${substituted}
        </body>
      </html>
    `;
  }, [getHtmlContent]);
  
  // Insert variable at cursor
  const insertVariable = useCallback((variable) => {
    if (editor) {
      editor.chain().focus().insertContent(variable).run();
    }
    setVariablesOpen(false);
  }, [editor]);
  
  // Handle save
  const handleSave = () => {
    const bodyContent = editor?.getText() ? getHtmlContent() : "";
    
    // Also save the raw editor content for re-editing
    const rawBody = editor?.getText() || "";
    
    onSave({
      ...template,
      name,
      subject,
      body: rawBody, // Save plain text for re-editing
      body_html: bodyContent, // Save HTML for rendering
      trigger_type: triggerType === "none" ? null : triggerType,
      send_delay_hours: sendDelayHours,
      is_active: isActive
    });
  };
  
  // Handle test email
  const handleSendTest = () => {
    onSendTest(template?.template_id);
  };
  
  // Variable categories
  const variableCategories = [
    {
      name: "Client Info",
      items: variables.filter(v => v.key.includes("client"))
    },
    {
      name: "Event Details",
      items: variables.filter(v => ["event", "venue", "days"].some(k => v.key.includes(k)))
    },
    {
      name: "Photographer",
      items: variables.filter(v => v.key.includes("photographer"))
    },
    {
      name: "Pricing",
      items: variables.filter(v => ["quote", "deposit", "balance"].some(k => v.key.includes(k)))
    },
    {
      name: "Links",
      items: variables.filter(v => v.key.includes("link"))
    }
  ];
  
  return (
    <div className="flex flex-col h-full max-h-[85vh]" data-testid="email-template-editor">
      {/* Header - Mobile Responsive */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 px-4 sm:px-6 py-3 sm:py-4 border-b border-border">
        <div className="flex items-center gap-3 sm:gap-4">
          <Button variant="ghost" onClick={onCancel} size="sm">
            <X className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Close</span>
          </Button>
          <div className="hidden sm:block h-4 w-px bg-border" />
          <span className="text-sm text-muted-foreground">
            {template?.template_id ? "Edit Template" : "New Template"}
          </span>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          {template?.template_id && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleSendTest}
              disabled={sendingTest}
              className="flex-1 sm:flex-none"
            >
              {sendingTest ? (
                <Loader2 className="w-4 h-4 sm:mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 sm:mr-2" />
              )}
              <span className="hidden sm:inline">Send Test</span>
            </Button>
          )}
          <Button onClick={handleSave} disabled={saving} size="sm" className="flex-1 sm:flex-none">
            {saving ? (
              <Loader2 className="w-4 h-4 sm:mr-2 animate-spin" />
            ) : (
              <Check className="w-4 h-4 sm:mr-2" />
            )}
            <span className="hidden sm:inline">Save</span>
            <span className="sm:hidden">Save</span>
          </Button>
        </div>
      </div>
      
      {/* Mobile Tab Switcher - Only visible on small screens */}
      <div className="flex md:hidden border-b border-border">
        <button
          type="button"
          onClick={() => setMobileTab("edit")}
          className={`flex-1 py-3 text-sm font-medium transition-colors ${
            mobileTab === "edit" 
              ? "text-primary border-b-2 border-primary bg-muted/30" 
              : "text-muted-foreground"
          }`}
        >
          Edit
        </button>
        <button
          type="button"
          onClick={() => setMobileTab("preview")}
          className={`flex-1 py-3 text-sm font-medium transition-colors ${
            mobileTab === "preview" 
              ? "text-primary border-b-2 border-primary bg-muted/30" 
              : "text-muted-foreground"
          }`}
        >
          Preview
        </button>
      </div>
      
      {/* Main Content - Split Screen on Desktop, Tabs on Mobile */}
      <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
        {/* Left Panel - Editor (always visible on desktop, conditional on mobile) */}
        <div className={`flex-1 flex flex-col border-r border-border overflow-hidden ${
          mobileTab === "edit" ? "flex" : "hidden md:flex"
        }`}>
          {/* Template Name & Subject */}
          <div className="p-4 space-y-4 border-b border-border">
            <div>
              <Label htmlFor="template-name" className="text-xs text-muted-foreground">
                Template Name
              </Label>
              <Input
                id="template-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Booking Confirmation"
                className="mt-1"
                data-testid="template-name-input"
              />
            </div>
            <div>
              <Label htmlFor="template-subject" className="text-xs text-muted-foreground">
                Email Subject
              </Label>
              <Input
                id="template-subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder='e.g., Your {{event_type}} is Confirmed!'
                className="mt-1"
                data-testid="template-subject-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Tip: Use variables like {'{{client_name}}'} in the subject
              </p>
            </div>
          </div>
          
          {/* Toolbar - Responsive */}
          <div className="flex items-center gap-1 p-2 border-b border-border bg-muted/30 flex-wrap overflow-x-auto">
            <ToolbarButton
              onClick={() => editor?.chain().focus().toggleBold().run()}
              isActive={editor?.isActive("bold")}
              tooltip="Bold"
            >
              <Bold className="w-4 h-4" />
            </ToolbarButton>
            
            <ToolbarButton
              onClick={() => editor?.chain().focus().toggleItalic().run()}
              isActive={editor?.isActive("italic")}
              tooltip="Italic"
            >
              <Italic className="w-4 h-4" />
            </ToolbarButton>
            
            <ToolbarButton
              onClick={() => editor?.chain().focus().toggleUnderline().run()}
              isActive={editor?.isActive("underline")}
              tooltip="Underline"
            >
              <UnderlineIcon className="w-4 h-4" />
            </ToolbarButton>
            
            <div className="h-4 w-px bg-border mx-1" />
            
            <Popover open={showLinkDialog} onOpenChange={setShowLinkDialog}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className={`p-2 rounded-md transition-colors ${
                    editor?.isActive("link") 
                      ? "bg-primary text-primary-foreground" 
                      : "hover:bg-muted"
                  }`}
                >
                  <LinkIcon className="w-4 h-4" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-64 p-0">
                {editor && (
                  <LinkDialog 
                    editor={editor} 
                    onClose={() => setShowLinkDialog(false)} 
                  />
                )}
              </PopoverContent>
            </Popover>
            
            <div className="h-4 w-px bg-border mx-1" />
            
            <ToolbarButton
              onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}
              isActive={editor?.isActive("heading", { level: 2 })}
              tooltip="Heading"
            >
              <Heading2 className="w-4 h-4" />
            </ToolbarButton>
            
            <ToolbarButton
              onClick={() => editor?.chain().focus().toggleBulletList().run()}
              isActive={editor?.isActive("bulletList")}
              tooltip="Bullet List"
            >
              <List className="w-4 h-4" />
            </ToolbarButton>
            
            <ToolbarButton
              onClick={() => editor?.chain().focus().toggleOrderedList().run()}
              isActive={editor?.isActive("orderedList")}
              tooltip="Numbered List"
            >
              <ListOrdered className="w-4 h-4" />
            </ToolbarButton>
            
            <ToolbarButton
              onClick={() => editor?.chain().focus().toggleBlockquote().run()}
              isActive={editor?.isActive("blockquote")}
              tooltip="Quote"
            >
              <Quote className="w-4 h-4" />
            </ToolbarButton>
            
            <ToolbarButton
              onClick={() => editor?.chain().focus().setHorizontalRule().run()}
              tooltip="Divider"
            >
              <Minus className="w-4 h-4" />
            </ToolbarButton>
            
            <div className="h-4 w-px bg-border mx-1" />
            
            <ToolbarButton
              onClick={() => editor?.chain().focus().undo().run()}
              disabled={!editor?.can().undo()}
              tooltip="Undo"
            >
              <Undo className="w-4 h-4" />
            </ToolbarButton>
            
            <ToolbarButton
              onClick={() => editor?.chain().focus().redo().run()}
              disabled={!editor?.can().redo()}
              tooltip="Redo"
            >
              <Redo className="w-4 h-4" />
            </ToolbarButton>
            
            <div className="flex-1" />
            
            {/* Variables Dropdown */}
            <Popover open={variablesOpen} onOpenChange={setVariablesOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2">
                  <Variable className="w-4 h-4" />
                  Insert Variable
                  <ChevronDown className="w-3 h-3" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-72 p-0 max-h-[400px] overflow-y-auto" align="end">
                <div className="p-2">
                  {variableCategories.map((category) => (
                    category.items.length > 0 && (
                      <div key={category.name} className="mb-3">
                        <p className="text-xs font-medium text-muted-foreground px-2 mb-1">
                          {category.name}
                        </p>
                        {category.items.map((v) => (
                          <button
                            key={v.key}
                            type="button"
                            onClick={() => insertVariable(v.key)}
                            className="w-full text-left px-2 py-1.5 rounded-md hover:bg-muted transition-colors"
                          >
                            <code className="text-xs text-primary">{v.key}</code>
                            <p className="text-xs text-muted-foreground">{v.label}</p>
                          </button>
                        ))}
                      </div>
                    )
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </div>
          
          {/* Editor Area */}
          <div className="flex-1 overflow-y-auto bg-background">
            <EditorContent 
              editor={editor} 
              className="h-full"
              data-testid="template-body-editor"
            />
          </div>
          
          {/* Automation Settings (Collapsible) */}
          <Collapsible open={automationOpen} onOpenChange={setAutomationOpen}>
            <CollapsibleTrigger asChild>
              <button 
                type="button"
                className="w-full flex items-center justify-between p-3 border-t border-border bg-muted/30 hover:bg-muted/50 transition-colors"
              >
                <span className="text-sm font-medium flex items-center gap-2">
                  Automation Settings
                  {triggerType !== "none" && (
                    <Badge variant="secondary" className="text-xs">
                      {triggers.find(t => t.type === triggerType)?.name || triggerType}
                    </Badge>
                  )}
                </span>
                <ChevronDown className={`w-4 h-4 transition-transform ${automationOpen ? "rotate-180" : ""}`} />
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="p-4 border-t border-border bg-muted/20 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-muted-foreground">Trigger</Label>
                    <Select
                      value={triggerType}
                      onValueChange={setTriggerType}
                    >
                      <SelectTrigger className="mt-1" data-testid="trigger-select">
                        <SelectValue placeholder="Select trigger..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No trigger (manual only)</SelectItem>
                        {triggers.map((trigger) => (
                          <SelectItem key={trigger.type} value={trigger.type}>
                            {trigger.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label className="text-xs text-muted-foreground">Send Delay</Label>
                    <Select
                      value={String(sendDelayHours)}
                      onValueChange={(v) => setSendDelayHours(parseInt(v))}
                    >
                      <SelectTrigger className="mt-1" data-testid="delay-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">Immediately</SelectItem>
                        <SelectItem value="1">1 hour later</SelectItem>
                        <SelectItem value="24">1 day later</SelectItem>
                        <SelectItem value="72">3 days later</SelectItem>
                        <SelectItem value="168">1 week later</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <Switch
                    checked={isActive}
                    onCheckedChange={setIsActive}
                    data-testid="active-toggle"
                  />
                  <Label className="text-sm">Template is active</Label>
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>
        </div>
        
        {/* Right Panel - Preview (always visible on desktop, conditional on mobile) */}
        <div className={`w-full md:w-[400px] flex flex-col bg-muted/20 ${
          mobileTab === "preview" ? "flex" : "hidden md:flex"
        }`}>
          {/* Preview Header */}
          <div className="flex items-center justify-between p-3 border-b border-border">
            <span className="text-sm font-medium">Live Preview</span>
            <div className="flex items-center gap-1 bg-muted rounded-md p-1">
              <button
                type="button"
                onClick={() => setPreviewDevice("desktop")}
                className={`p-1.5 rounded ${previewDevice === "desktop" ? "bg-background" : ""}`}
              >
                <Monitor className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => setPreviewDevice("mobile")}
                className={`p-1.5 rounded ${previewDevice === "mobile" ? "bg-background" : ""}`}
              >
                <Smartphone className="w-4 h-4" />
              </button>
            </div>
          </div>
          
          {/* Preview Subject */}
          <div className="p-3 border-b border-border bg-card">
            <p className="text-xs text-muted-foreground mb-1">Subject:</p>
            <p className="text-sm font-medium">
              {substituteVariables(subject || "No subject")}
            </p>
          </div>
          
          {/* Preview Content */}
          <div className="flex-1 p-3 overflow-y-auto">
            <div className={`bg-card rounded-lg border border-border overflow-hidden ${
              previewDevice === "mobile" ? "max-w-[320px] mx-auto" : ""
            }`}>
              <iframe
                srcDoc={getPreviewHtml()}
                className="w-full h-[400px] border-0"
                title="Email Preview"
                data-testid="preview-iframe"
              />
            </div>
            
            {/* Sample Data Notice */}
            <div className="mt-4 p-3 bg-muted/50 rounded-lg">
              <p className="text-xs text-muted-foreground">
                <Eye className="w-3 h-3 inline mr-1" />
                Preview uses sample data. Variables will be replaced with real client data when sent.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailTemplateEditor;
